import { CircularProgress, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { forwardRef, useCallback, useContext, useEffect } from "react";
import { useState } from "react";
import { useTranslate } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  MaintenanceDashboardApiFactory,
  UnresolvedDvirDefectsDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { SelectedVehiclesFilterContext, WidgetProps } from "../common";
import { Widget } from "./Widget";

const useStyles = makeStyles((theme: Theme) => ({
  major: {
    color: theme.palette.error.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.error.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
  minor: {
    color: theme.palette.warning.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.warning.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
}));

export const UnresolvedDvirDefects = forwardRef(
  ({ className, icon, title, ...rest }: WidgetProps, ref) => {
    const translate = useTranslate();
    const classes = useStyles();
    const [dvirDefects, setDvirDefects] = useState<UnresolvedDvirDefectsDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    const filterContext = useContext(SelectedVehiclesFilterContext);
    useEffect(() => {
      if (!axios) {
        return;
      }

      setLoading((x) => true);
      MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppMaintenancedashboardUnresolveddvirdefectsGet()
        .then((result) => {
          setDvirDefects(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios]);

    const handleMajorClick = useCallback(() => {
      if (!filterContext || !dvirDefects?.major) {
        return;
      }

      filterContext.setSelectedFilter("WITH_MAJOR_UNRESOLVED_DVIR_DEFECTS");
    }, [filterContext, dvirDefects]);

    const handleMinorClick = useCallback(() => {
      if (!filterContext || !dvirDefects?.minor) {
        return;
      }

      filterContext.setSelectedFilter("WITH_MINOR_UNRESOLVED_DVIR_DEFECTS");
    }, [filterContext, dvirDefects]);

    const { minor, major } = dvirDefects ? dvirDefects : { minor: 0, major: 0 };
    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className="categories">
          {loading ? (
            <div className="category">
              <CircularProgress size="3rem" />
            </div>
          ) : (
            <>
              <div className="category">
                <div
                  className={`category-value ${classes.major}`}
                  onClick={handleMajorClick}
                >
                  {major}
                </div>
                <div className="category-title">
                  {translate(
                    "resources.maintenance-dashboard.labels.majorDefects"
                  )}
                </div>
              </div>
              <div className="category">
                <div
                  className={`category-value ${classes.minor}`}
                  onClick={handleMinorClick}
                >
                  {minor}
                </div>
                <div className="category-title">
                  {translate(
                    "resources.maintenance-dashboard.labels.minorDefects"
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
